import React from 'react';
import moment from 'moment-timezone';
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import styles from './EditItinerary.module.css';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../../Components/PageContainer';
import { SaveItineraryModal } from '../SaveItineraryModal';
import { Summary } from './Summary';
import { ItemCard } from './ItemCard/ItemCard';
import { useMapDisplayContext } from 'components/Map/MapDisplayContext';
import { useSelector } from 'react-redux';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { AnimatedButton } from 'components/Map/AnimatedButton';
import { useItineraries } from 'components/Map/useItineraries';
import { DateSection } from './DateSection/DateSection';
import { DateItinerary, Itinerary } from 'models/itinerary';
import { getWalkingTimesByPinKey } from '../EditItineraryModal/generateItinerary';
import { formattedDate, formattedTimeOfDay } from 'components/Map/util';
interface EditItineraryProps {
  onBackClick: () => void;
  onEditDetailsClick: () => void;
  onSaveClick: () => void;
}

// Helper functions
const findItemPosition = (
  dates: DateItinerary[],
  pinKey: string
): [dateIndex: number, itemIndex: number] => {
  for (let dateIndex = 0; dateIndex < dates.length; dateIndex++) {
    const itemIndex = dates[dateIndex].items.findIndex((item) => item.pin_key === pinKey);
    if (itemIndex !== -1) {
      return [dateIndex, itemIndex];
    }
  }
  return [-1, -1];
};

const isDateContainer = (id: string): boolean => {
  return moment(id, 'YYYY-MM-DD', true).isValid();
};

const getDateString = (itinerary: Itinerary | null, language: string) => {
  if (!itinerary?.date_itineraries.length) return '';

  const firstDate = itinerary.date_itineraries[0].date;
  const lastDate = itinerary.date_itineraries[itinerary.date_itineraries.length - 1].date;

  if (firstDate === lastDate) {
    return formattedDate(firstDate, 'D MMM YYYY', language);
  }

  return `${formattedDate(firstDate, 'D MMM', language)} - ${formattedDate(
    lastDate,
    'D MMM',
    language
  )}`;
};

export const EditItinerary: React.FC<EditItineraryProps> = ({
  onBackClick,
  onEditDetailsClick,
  onSaveClick,
}) => {
  const { t, i18n } = useTranslation();
  const [showSaveModal, setShowSaveModal] = React.useState(false);
  const { setNewItinerary, newItinerary } = useMapDisplayContext();
  const digitalMap = useSelector(selectDigitalMap);
  const [activeId, setActiveId] = React.useState<string | null>(null);

  const { createItinerary, updateItinerary, itineraries } = useItineraries();

  // Find the active item for the drag overlay
  const activeItem = React.useMemo(() => {
    if (!activeId || !newItinerary) return null;

    const item = newItinerary.date_itineraries
      .flatMap((date) => date.items)
      .find((item) => item.pin_key === activeId);
    if (!item) return null;
    return {
      id: item.pin_key,
      time: formattedTimeOfDay(item.time, i18n.language),
      title: digitalMap?.pins?.find((pin) => pin.key === item.pin_key)?.title ?? '',
      duration: item.duration,
      walkingTime: item.travel_time,
    };
  }, [activeId, newItinerary, digitalMap?.pins]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || !newItinerary) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Make a copy of the dates array
    const updatedDates = [...newItinerary.date_itineraries];
    const [srcDateIdx, srcItemIdx] = findItemPosition(updatedDates, activeId);

    if (srcDateIdx === -1) return;

    // Case 1: Dragging over a date container
    if (isDateContainer(overId)) {
      const targetDateIdx = updatedDates.findIndex((date) => date.date === overId);
      if (targetDateIdx === -1) return;

      // Move item to end of target date
      const [movedItem] = updatedDates[srcDateIdx].items.splice(srcItemIdx, 1);
      updatedDates[targetDateIdx].items.push(movedItem);
    }
    // Case 2: Dragging over another item
    else {
      const [destDateIdx, destItemIdx] = findItemPosition(updatedDates, overId);
      if (destDateIdx === -1) return;

      // Move the item
      const [movedItem] = updatedDates[srcDateIdx].items.splice(srcItemIdx, 1);
      updatedDates[destDateIdx].items.splice(destItemIdx, 0, movedItem);
    }

    // Update the preview
    setNewItinerary({
      ...newItinerary,
      date_itineraries: updatedDates,
    });
  };

  const handleDragEnd = async () => {
    if (!activeId || !newItinerary || !digitalMap) return;

    // Update itinerary times
    const updatedDates = [...newItinerary.date_itineraries];
    const [dateIdx, itemIdx] = findItemPosition(updatedDates, activeId as string);
    if (dateIdx === -1 || itemIdx === -1) return;

    updatedDates[dateIdx].items[itemIdx].time = '';

    const allPinKeys = updatedDates.flatMap((date) => date.items.map((item) => item.pin_key));
    const walkingTimesByPinKey = await getWalkingTimesByPinKey(digitalMap, allPinKeys);

    updatedDates.forEach((date) => {
      date.items.forEach((item, idx) => {
        item.travel_time = idx === 0 ? '-' : walkingTimesByPinKey[item.pin_key];
      });
    });

    setNewItinerary({
      ...newItinerary,
      date_itineraries: updatedDates,
    });

    setActiveId(null);
  };

  const date = getDateString(newItinerary, i18n.language);

  return (
    <>
      <PageContainer
        onBackClick={onBackClick}
        headerContent={<h1 className={styles.title}>{t('Edit Itinerary')}</h1>}
      >
        <div className={styles.container}>
          <Summary
            date={date}
            pins={
              newItinerary?.date_itineraries.reduce((acc, date) => acc + date.items.length, 0) ?? 0
            }
            onEditClick={onEditDetailsClick}
          />

          <DndContext
            sensors={sensors}
            collisionDetection={rectIntersection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            {newItinerary?.date_itineraries.map((date, dateIndex) => (
              <DateSection
                key={date.date}
                date={date}
                dayNumber={dateIndex + 1}
                onDeleteItem={(itemIndex) => {
                  setNewItinerary({
                    ...newItinerary,
                    date_itineraries: newItinerary.date_itineraries.map((d) =>
                      d.date === date.date
                        ? {
                            ...d,
                            items: d.items.filter((_, index) => index !== itemIndex),
                          }
                        : d
                    ),
                  });
                }}
                onTimeChange={async (itemIndex, newTime) => {
                  const dateIndex = newItinerary.date_itineraries.findIndex(
                    (d) => d.date === date.date
                  );
                  if (dateIndex === -1 || !digitalMap) return;

                  const updatedItems = newItinerary.date_itineraries[dateIndex].items
                    .map((item, i) =>
                      i === itemIndex
                        ? { ...item, time: moment(newTime, 'HH:mm').format('HH:mm') }
                        : item
                    )
                    .sort((a, b) => {
                      if (!a.time && !b.time) return 0;
                      if (!a.time) return 1;
                      if (!b.time) return -1;
                      return a.time.localeCompare(b.time);
                    });

                  const walkingTimesByPinKey = await getWalkingTimesByPinKey(
                    digitalMap,
                    updatedItems.map((item) => item.pin_key)
                  );

                  updatedItems.forEach((item, idx) => {
                    item.travel_time = idx === 0 ? '-' : walkingTimesByPinKey[item.pin_key];
                  });

                  setNewItinerary({
                    ...newItinerary,
                    date_itineraries: newItinerary.date_itineraries.map((d) =>
                      d.date === date.date
                        ? {
                            ...d,
                            items: updatedItems,
                          }
                        : d
                    ),
                  });
                }}
              />
            ))}

            <DragOverlay>
              {activeItem && (
                <ItemCard
                  item={{
                    pin_key: activeItem.id,
                    time: activeItem.time,
                    duration: activeItem.duration,
                    travel_time: activeItem.walkingTime,
                  }}
                  onDelete={() => {}}
                  onTimeChange={() => {}}
                  date=""
                  defaultTime=""
                />
              )}
            </DragOverlay>
          </DndContext>

          <AnimatedButton
            className={styles.saveButton}
            clickedClassName={styles.saveButtonClicked}
            onClick={() => setShowSaveModal(true)}
          >
            {t('Save Itinerary')}
          </AnimatedButton>
        </div>
      </PageContainer>
      {showSaveModal && (
        <SaveItineraryModal
          onClose={() => {
            setShowSaveModal(false);
          }}
          onSave={(name: string) => {
            if (!newItinerary) return;

            if (itineraries?.some((itinerary) => itinerary.id === newItinerary?.id)) {
              updateItinerary(newItinerary.id, {
                title: name,
                digital_map_id: digitalMap?.id ?? '',
                date_itineraries: newItinerary?.date_itineraries ?? [],
              });
            } else {
              createItinerary({
                title: name,
                digital_map_id: digitalMap?.id ?? '',
                date_itineraries: newItinerary?.date_itineraries ?? [],
              });
            }

            setNewItinerary(null);

            onSaveClick();
          }}
        />
      )}
    </>
  );
};
